import { useQueryClient } from "@tanstack/react-query";
import { ArrowRight, Building2, LogOut, ShieldCheck } from "lucide-react";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { identity, identityEnabled, type Discovery } from "../api/identity";
import { completeSignIn, useAuth } from "../auth/auth";
import { config, landingUrl } from "../config";
import { retryDelaySeconds } from "../lib/retryDelay";
import { ssoClientId } from "../lib/ssoClient";
import { Alert, Button, Field, Spinner, Switch, TextInput } from "../components/ui";
import { useT, useIntlShape } from "../i18n/I18nProvider";
import { DirIcon } from "../i18n/mirror";

function Art() {
  const t = useT();
  const intl = useIntlShape();
  return (
    <div className="auth-art">
      <a href={landingUrl()} className="brand" style={{ padding: 0 }} title={t("shell.brand.website")}>
        <img src="/solaros-mark.png" alt="" />
        <div>
          <b>SolarOS</b>
        </div>
      </a>
      <div>
        <h2>{intl.formatMessage({ id: "auth.art.headline" }, { em: (c) => <span className="gradient-text">{c}</span> })}</h2>
        <p>{t("auth.art.body")}</p>
      </div>
      <img className="roof" src="/roof-design.webp" alt={t("auth.art.imageAlt")} />
    </div>
  );
}

/** Discovery for `address`, asked again once after 1.5 s when it could not answer; still `unknown`: the caller waits
 * (lib/retryDelay.ts) and never falls back to the password page. */
async function discoverWithRetry(address: string): Promise<Discovery> {
  let found = await identity.discover(address);
  if (found.type === "unknown") {
    await new Promise((r) => setTimeout(r, 1500));
    found = await identity.discover(address);
  }
  return found;
}

/** Starts the company sign-in discovery found: External ID with `domain_hint` through the SSO app, or (development,
 * no PKCE client of its own) discovery's ready-made authorize URL. */
function useCompanySignIn(returnTo: string) {
  const auth = useAuth();
  return async (found: Extract<Discovery, { type: "sso" }>, address: string) => {
    if (auth.mode === "oidc")
      await auth.signIn({ returnTo, loginHint: address, domainHint: found.domain_hint, ssoClientId: ssoClientId(config.ssoClientId, found.login_url) });
    else window.location.assign(found.login_url);
  };
}

/** Email first, for every sign-in. On Continue the identity service says whether the address belongs to a company
 * with single sign-on. If it does, we go straight to External ID with `domain_hint` (so to the company's own sign-in
 * page) and show only "Taking you to your company sign-in…"; otherwise the normal sign-in continues. Nobody picks
 * "SSO" or sees a list of companies. "Use another sign-in method" skips discovery once (break-glass owners). */
function EmailFirstForm({ email, setEmail, onPassword, onSso, label, children, footer }: {
  email: string;
  setEmail: (email: string) => void;
  onPassword: (email: string) => Promise<void> | void;
  onSso: (found: Extract<Discovery, { type: "sso" }>, email: string) => Promise<void> | void;
  label?: string;
  children?: ReactNode;
  footer?: ReactNode;
}) {
  const t = useT();
  const [busy, setBusy] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const skipDiscovery = useRef(false);
  // The check could not answer: how many times in a row, and until when Continue waits (20 s more each time + jitter).
  const [failures, setFailures] = useState(0);
  const [waitUntil, setWaitUntil] = useState(0);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (waitUntil <= Date.now()) return;
    const timer = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(timer);
  }, [waitUntil]);
  const waitSeconds = Math.max(0, Math.ceil((waitUntil - now) / 1000));
  const form = useRef<HTMLFormElement>(null);
  if (redirecting)
    return (
      <div className="center-fill" role="status" style={{ minHeight: 160 }} data-testid="sso-redirecting">
        <div className="row muted">
          <span className="spinner" /> {t("auth.sso.redirecting")}
        </div>
      </div>
    );
  return (
    <form
      ref={form}
      className="stack"
      onSubmit={async (e) => {
        e.preventDefault();
        const address = email.trim().toLowerCase();
        const skip = skipDiscovery.current;
        skipDiscovery.current = false;
        if (waitSeconds > 0 && !skip) return; // "Use another sign-in method" still works while Continue waits
        setBusy(true);
        try {
          const found: Discovery = skip ? { type: "password" } : await discoverWithRetry(address);
          if (found.type === "unknown") {
            const next = failures + 1;
            setFailures(next);
            setNow(Date.now());
            setWaitUntil(Date.now() + retryDelaySeconds(next) * 1000);
            return;
          }
          setFailures(0);
          if (found.type === "sso") {
            setRedirecting(true);
            await onSso(found, address);
          } else await onPassword(address);
        } catch {
          setRedirecting(false);
        } finally {
          setBusy(false);
        }
      }}
    >
      <Field label={label ?? t("auth.workEmail")}>
        <TextInput type="email" required autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} autoFocus placeholder={t("auth.emailPlaceholder")} dir="auto" />
      </Field>
      {children}
      {failures > 0 && (
        <div data-testid="discovery-unknown">
          <Alert tone="warn">
            {waitSeconds > 0 ? t("auth.discovery.unknownWait", { seconds: waitSeconds }) : t("auth.discovery.unknownContinue")}
          </Alert>
        </div>
      )}
      <Button type="submit" variant={waitSeconds > 0 ? "default" : "primary"} size="lg" block icon={<DirIcon><ArrowRight /></DirIcon>} loading={busy} disabled={waitSeconds > 0}>
        {waitSeconds > 0 ? t("auth.discovery.retryIn", { seconds: waitSeconds }) : t("common.continue")}
      </Button>
      {(footer || identityEnabled()) && (
        <p className="small muted">
          {footer}
          {footer && identityEnabled() && " · "}
          {identityEnabled() && (
            <button
              type="button"
              className="link-btn"
              onClick={() => {
                skipDiscovery.current = true;
                form.current?.requestSubmit();
              }}
            >
              {t("auth.anotherMethod")}
            </button>
          )}
        </p>
      )}
    </form>
  );
}

function OidcSignIn({ signup, returnTo }: { signup?: boolean; returnTo: string }) {
  const t = useT();
  const auth = useAuth();
  const companySignIn = useCompanySignIn(returnTo);
  const [email, setEmail] = useState("");
  if (signup || !identityEnabled())
    return (
      <div className="stack">
        <Button variant="primary" size="lg" block onClick={() => auth.signIn({ signup, returnTo })} icon={<DirIcon><ArrowRight /></DirIcon>}>
          {signup ? t("auth.signUpWithEmail") : t("auth.signIn")}
        </Button>
        <p className="small muted">
          {signup ? t("auth.haveAccount") : t("auth.newToSolarOs")}{" "}
          <button className="link-btn" onClick={() => auth.signIn({ signup: !signup, returnTo })}>
            {signup ? t("auth.signIn") : t("auth.createAccount")}
          </button>
        </p>
      </div>
    );
  return (
    <EmailFirstForm
      email={email}
      setEmail={setEmail}
      onPassword={(address) => auth.signIn({ returnTo, loginHint: address })}
      onSso={companySignIn}
      footer={
        <>
          {t("auth.newToSolarOs")}{" "}
          <button type="button" className="link-btn" onClick={() => auth.signIn({ signup: true, returnTo })}>
            {t("auth.createAccount")}
          </button>
        </>
      }
    />
  );
}

export function Login({ signup }: { signup?: boolean }) {
  const t = useT();
  const intl = useIntlShape();
  const auth = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const returnTo = (location.state as { returnTo?: string } | null)?.returnTo ?? "/";
  const companySignIn = useCompanySignIn(returnTo);
  const [email, setEmail] = useState("owner@sunrise-solar.test");
  const [admin, setAdmin] = useState(false);

  if (auth.ready && auth.signedIn) return <Navigate to={returnTo} replace />;

  return (
    <div className="auth-page">
      <Art />
      <div className="auth-form">
        <div className="auth-card">
          <div>
            <h1>{signup ? t("auth.signup.title") : t("auth.login.title")}</h1>
            <p className="muted" style={{ marginTop: 6 }}>
              {signup ? t("auth.signup.lead") : t("auth.login.lead")}
            </p>
          </div>
          {auth.mode === "oidc" ? (
            <OidcSignIn signup={signup} returnTo={returnTo} />
          ) : (
            <>
              <Alert tone="warn" title={t("auth.dev.title")}>
                {intl.formatMessage({ id: "auth.dev.body" }, { code: (c) => <code>{c}</code> })}
              </Alert>
              <EmailFirstForm
                label={t("auth.email")}
                email={email}
                setEmail={setEmail}
                onPassword={(address) => {
                  qc.clear();
                  auth.devSignIn({ email: address, platformAdmin: admin });
                  navigate(returnTo, { replace: true });
                }}
                onSso={companySignIn}
              >
                <Switch checked={admin} onChange={setAdmin} label={<span className="row" style={{ gap: 6 }}><ShieldCheck size={16} /> {t("auth.dev.platformAdmin")}</span>} />
              </EmailFirstForm>
            </>
          )}
          <p className="small faint">{t("auth.terms")}</p>
        </div>
      </div>
    </div>
  );
}

/** The API refused this sign-in (403 `sso_required`): it is a new password, Google or Microsoft sign-in of an email
 * whose company signs in with single sign-on. One button runs the email-first discovery for that email and starts the
 * company sign-in; nobody is left on an error page. If discovery no longer says SSO (turned off meanwhile), the button
 * asks the API again (`onRetry`). */
export function SsoRequiredScreen({ email, onRetry }: { email: string | null; onRetry: () => void }) {
  const t = useT();
  const intl = useIntlShape();
  const auth = useAuth();
  const location = useLocation();
  const companySignIn = useCompanySignIn(location.pathname + location.search);
  const [busy, setBusy] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [failures, setFailures] = useState(0);
  const [waitUntil, setWaitUntil] = useState(0);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (waitUntil <= Date.now()) return;
    const timer = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(timer);
  }, [waitUntil]);
  const waitSeconds = Math.max(0, Math.ceil((waitUntil - now) / 1000));

  const start = async () => {
    if (!email || waitSeconds > 0) return;
    setBusy(true);
    try {
      const found = await discoverWithRetry(email);
      if (found.type === "unknown") {
        const next = failures + 1;
        setFailures(next);
        setNow(Date.now());
        setWaitUntil(Date.now() + retryDelaySeconds(next) * 1000);
        return;
      }
      setFailures(0);
      if (found.type === "sso") {
        setRedirecting(true);
        await companySignIn(found, email);
      } else onRetry();
    } catch {
      setRedirecting(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="auth-page" data-testid="sso-required">
      <Art />
      <div className="auth-form">
        <div className="auth-card">
          <div>
            <h1>{t("auth.sso.title")}</h1>
            <p className="muted" style={{ marginTop: 6 }}>
              {email
                ? intl.formatMessage({ id: "auth.sso.emailBelongs" }, { email, b: (c) => <b dir="auto">{c}</b> })
                : t("auth.sso.yourEmailBelongs")}
            </p>
          </div>
          {redirecting ? (
            <div className="center-fill" role="status" style={{ minHeight: 120 }} data-testid="sso-redirecting">
              <div className="row muted">
                <span className="spinner" /> {t("auth.sso.redirecting")}
              </div>
            </div>
          ) : (
            <div className="stack">
              <Alert tone="info" title={t("auth.sso.requiredTitle")}>
                {email ? t("auth.sso.requiredContinue") : t("auth.sso.requiredSignInAgain")}
              </Alert>
              {failures > 0 && (
                <div data-testid="discovery-unknown">
                  <Alert tone="warn">
                    {waitSeconds > 0 ? t("auth.discovery.unknownWait", { seconds: waitSeconds }) : t("auth.discovery.unknownButton")}
                  </Alert>
                </div>
              )}
              {email ? (
                <Button variant={waitSeconds > 0 ? "default" : "primary"} size="lg" block icon={<Building2 />} loading={busy} disabled={waitSeconds > 0} onClick={start}>
                  {waitSeconds > 0 ? t("auth.discovery.retryIn", { seconds: waitSeconds }) : t("auth.sso.continue")}
                </Button>
              ) : null}
              <Button variant="ghost" block icon={<DirIcon><LogOut /></DirIcon>} onClick={() => auth.signOut()}>
                {email ? t("auth.sso.signOutOther") : t("auth.sso.signOutWorkEmail")}
              </Button>
            </div>
          )}
          <p className="small faint">{t("auth.sso.wrongHelp")}</p>
        </div>
      </div>
    </div>
  );
}

export function AuthCallback() {
  const t = useT();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    completeSignIn()
      .then((user) => {
        const state = user.state as { returnTo?: string } | undefined;
        window.location.replace(state?.returnTo ?? "/");
      })
      .catch((e: Error) => setError(e.message));
  }, [navigate]);
  if (error)
    return (
      <div className="center-fill" style={{ minHeight: "100vh", padding: 16 }}>
        <div className="stack" style={{ maxWidth: 440 }}>
          <Alert tone="danger" title={t("auth.callback.failed")}>
            {error}
          </Alert>
          <Button onClick={() => navigate("/login")}>{t("auth.callback.back")}</Button>
        </div>
      </div>
    );
  return <Spinner label={t("auth.signingIn")} />;
}
